import { c as _c } from "react/compiler-runtime";
import { IcoChevronDown16 } from '@onefootprint/icons';
import type { PlaybookVersionNoGraph } from '@onefootprint/request-types/dashboard';
import { AnimatePresence, easeInOut, motion } from 'framer-motion';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CurlPreview from '@/components/curl-preview';
import useSession from '@/hooks/use-session';
import type { CreateOnboardingBody, RunFormValues } from '../../onboarding.types';
import { buildOnboardingBody } from '../../onboarding.utils';

// Stand-in for the fp_id until a test user is picked, so the curl is complete from the first render.
const FP_ID_PLACEHOLDER = '<FP_ID>';
const expandCollapse = {
  initial: {
    height: 0,
    opacity: 0
  },
  animate: {
    height: 'auto',
    opacity: 1
  },
  exit: {
    height: 0,
    opacity: 0
  },
  transition: {
    duration: 0.2,
    ease: easeInOut
  }
};
type RunCurlPreviewProps = {
  savedFpId: string | null;
  playbook: PlaybookVersionNoGraph;
  hasPendingChanges: boolean;
  versionId?: string;
  defaultExpanded?: boolean;
};
export const RunCurlPreview = t0 => {
  const $ = _c(42);
  const {
    savedFpId,
    playbook,
    hasPendingChanges,
    versionId,
    defaultExpanded: t1
  } = t0;
  const defaultExpanded = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "testMode.runMode.run"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t2);
  const {
    isLive
  } = useSession();
  const {
    control
  } = useFormContext();
  let t3;
  if ($[1] !== control) {
    t3 = {
      control
    };
    $[1] = control;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const values = useWatch(t3);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [now] = useState(_temp);
  const t4 = savedFpId ?? FP_ID_PLACEHOLDER;
  let body;
  if ($[3] !== hasPendingChanges || $[4] !== isLive || $[5] !== now || $[6] !== playbook || $[7] !== t4 || $[8] !== values || $[9] !== versionId) {
    const base = {
      fpId: t4,
      playbook,
      isLive,
      hasPendingChanges,
      versionId,
      now
    };
    try {
      body = buildOnboardingBody({
        ...base,
        values
      });
    } catch {
      body = buildOnboardingBody({
        ...base,
        values: {
          ...values,
          prerequisiteData: undefined
        }
      });
    }
    $[3] = hasPendingChanges;
    $[4] = isLive;
    $[5] = now;
    $[6] = playbook;
    $[7] = t4;
    $[8] = values;
    $[9] = versionId;
    $[10] = body;
  } else {
    body = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setIsExpanded(_temp2);
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t) {
    t6 = t(_temp3);
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = <p className="text-label-3 text-secondary">{t6}</p>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t) {
    t8 = t(_temp4);
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== hasPendingChanges || $[19] !== t || $[20] !== versionId) {
    t9 = hasPendingChanges && versionId && t(_temp5);
    $[18] = hasPendingChanges;
    $[19] = t;
    $[20] = versionId;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8 || $[23] !== t9) {
    t10 = <p className="text-body-3 text-tertiary text-left">{t8} {t9}</p>;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t7) {
    t11 = <div className="flex flex-col items-start gap-1">{t7}{t10}</div>;
    $[25] = t10;
    $[26] = t7;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const t12 = isExpanded ? "transition-transform rotate-180" : "transition-transform";
  let t13;
  if ($[28] !== t12) {
    t13 = <IcoChevronDown16 className={t12} />;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== isExpanded || $[31] !== t11 || $[32] !== t13) {
    t14 = <button type="button" onClick={t5} aria-expanded={isExpanded} className="flex items-center justify-between cursor-pointer m-2">{t11}{t13}</button>;
    $[30] = isExpanded;
    $[31] = t11;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== body || $[35] !== isExpanded) {
    t15 = isExpanded && <motion.div {...expandCollapse} className="overflow-hidden"><div className="flex flex-col px-2 pb-2 gap-2 max-h-[25dvh] overflow-y-auto md:max-h-[40vh]"><CurlPreview method="POST" path="/onboardings" body={body} /></div></motion.div>;
    $[34] = body;
    $[35] = isExpanded;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t15) {
    t16 = <AnimatePresence initial={false}>{t15}</AnimatePresence>;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t14 || $[40] !== t16) {
    t17 = <div className="flex flex-col w-full gap-3">{t14}{t16}</div>;
    $[39] = t14;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  return t17;
};
function _temp() {
  return new Date().toISOString();
}
function _temp2(prev) {
  return !prev;
}
function _temp3($) {
  return $.viewCurlRequest;
}
function _temp4($_0) {
  return $_0.apiRequestHint;
}
function _temp5($_1) {
  return $_1.versionPinnedNote;
}